import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_LOCALE,
  type SupportedLocale,
  isSupportedLocale,
} from './types';

/**
 * Returns the current locale and a setter. The setter delegates to
 * i18next, which persists via the configured LanguageDetector cache
 * (localStorage). Consumers do not need to write to localStorage themselves.
 */
export function useLocale(): {
  locale: SupportedLocale;
  setLocale: (next: SupportedLocale) => void;
} {
  const { i18n } = useTranslation();

  const current = isSupportedLocale(i18n.language)
    ? i18n.language
    : DEFAULT_LOCALE;

  const setLocale = useCallback(
    (next: SupportedLocale) => {
      if (next === current) return;
      void i18n.changeLanguage(next);
    },
    [i18n, current],
  );

  return { locale: current, setLocale };
}
